import { DUR, STAGGER } from "@aybuben/client/motion.ts";
import type { HomeUnit } from "@aybuben/client/screens/home.ts";
import { newlyWalked, unitTrail, type TrailLayout } from "@aybuben/client/screens/trail.ts";
import { paletteFor } from "@aybuben/client/world/palette.ts";
import { sceneryLayer } from "@aybuben/client/world/trail-scenery.ts";
import type { UnitView } from "@aybuben/core/content/course.ts";
import type { LessonDef } from "@aybuben/core/content/types.ts";
import type { GameState } from "@aybuben/core/state.ts";
import { useEffect, useMemo, type Ref } from "react";

import { COL } from "../../theme.ts";
import { Drawing } from "../world/Drawing.tsx";
import { TrailNode } from "./TrailNode.tsx";

// The scenery keeps the summer-day palette, like the card art: the path stays still.
const SCENERY_PALETTE = paletteFor("summer", "day");

/** What each unit's trail looked like when last drawn: session memory, to animate what a lesson just walked. */
const drawn = new Map<string, TrailLayout>();

interface TrailBlockProps {
	hu: HomeUnit;
	st: GameState;
	width: number;
	/** Lands on the learner's place (or, during a unit focus, the unit block). */
	focusRef?: Ref<HTMLDivElement>;
	/** The jump button's target: the current lesson's node. */
	currentRef?: Ref<HTMLDivElement>;
	onLesson: (u: UnitView, lesson: LessonDef) => void;
}

export function TrailBlock({ hu, st, width, focusRef, currentRef, onLesson }: TrailBlockProps) {
	const { unit: u, lessons } = hu;
	const layout = unitTrail(hu, st, width);
	const walkedSig = layout.segments.map((s) => (s.walked ? 1 : 0)).join("");
	// Not keyed on width: Home measures the column after the first paint, and a fresh
	// that re-ran then would see `drawn` already updated and cancel the fill.
	const fresh = useMemo(
		// layout is derived from walkedSig and width
		// oxlint-disable-next-line react/memo-dependencies
		() => newlyWalked(drawn.get(u.id), layout),
		[u.id, walkedSig],
	);
	// Written after commit, so the render above still sees the previous trail.
	useEffect(() => {
		drawn.set(u.id, layout);
		// layout is derived from walkedSig and width
		// oxlint-disable-next-line react/exhaustive-deps
	}, [u.id, walkedSig, width]);
	const order = new Map(fresh.map((k, i) => [k, i]));
	return (
		<div className="relative mx-auto mt-4" style={{ width: layout.width, height: layout.height }}>
			<svg
				className="absolute inset-0"
				width={layout.width}
				height={layout.height}
				aria-hidden="true"
			>
				{layout.segments.map((s, k) => {
					const i = order.get(k);
					return (
						<path
							key={k}
							data-trail-seg=""
							d={s.d}
							fill="none"
							stroke={s.walked ? COL.apricot : COL.line}
							strokeOpacity={s.walked ? 0.55 : 1}
							strokeWidth={7}
							strokeLinecap="round"
							strokeDasharray="0 15"
							className={i === undefined ? undefined : "motion-trail"}
							style={i === undefined ? undefined : { animationDelay: `${i * STAGGER}ms` }}
						/>
					);
				})}
			</svg>
			{layout.scenery.map((spot, k) => (
				<div
					key={k}
					className="pointer-events-none absolute"
					style={{ left: spot.x, top: spot.y, width: spot.size, height: spot.size }}
				>
					<Drawing
						layer={sceneryLayer(spot)}
						palette={SCENERY_PALETTE}
						scale={spot.size / 100}
						width={spot.size}
						height={spot.size}
					/>
				</div>
			))}
			{lessons.map((l, i) => {
				// nodes and lessons are built from the same list
				const pos = layout.nodes[i]!;
				return (
					<TrailNode
						key={l.lesson.id}
						u={u}
						lesson={l.lesson}
						st={st}
						position={l.position}
						x={pos.x}
						y={pos.y}
						isCurrent={l.isCurrent}
						ringIn={fresh.length > 0}
						ringDelay={(fresh.length - 1) * STAGGER + DUR.slow}
						offline={l.offline}
						focusRef={l.isPlace ? focusRef : undefined}
						currentRef={l.isCurrent ? currentRef : undefined}
						onClick={() => onLesson(u, l.lesson)}
					/>
				);
			})}
		</div>
	);
}
